import { useMemo } from 'react';

import styled from '@emotion/styled';
import { Text, Colors, Flex } from '@revenuemarket/cds';
import { CustomAttribute } from '@revenuemarket/utils/customAttribute';
import { Tooltip } from 'antd';
import cx from 'classnames';
import { useRouter } from 'next/router';

import { css } from '~libs/emotion';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { mixpanel } from '~pages/_app';
import { appendUtmToUrlString } from '~utils/session/utmSession';

import useNavigationStore from './useNavigationStore';
export type NavigationStatus = 'selected' | 'disabled' | 'default';

export type NavigationItem = {
  title: string;
  activeKey: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  link?: string;
  dataType?: string;
  customAttributes?: CustomAttribute;
};

interface Props extends NavigationItem {
  style?: React.CSSProperties;
  className?: string;
  node?: React.ReactNode;
  customAttributes?: CustomAttribute;
  disabled?: boolean;
  tooltipMsg?: string;
  rightSlot?: React.ReactNode;
}

const getColor = (status: NavigationStatus) => {
  switch (status) {
    case 'selected':
      return Colors.gray800;
    case 'disabled':
      return Colors.grayOpacity36;
    default:
      return Colors.gray700;
  }
};

const Wrapper = styled(Flex)<{ status: NavigationStatus }>`
  border-radius: 8px;
  height: 32px;
  padding: 6px 0px;
  cursor: pointer;
  padding: 0 8px;
  background: ${({ status }) => {
    switch (status) {
      case 'selected':
        return Colors.grayOpacity07;
      default:
        return '';
    }
  }};
  & svg {
    // path안의 fill
    path {
      fill: ${({ status }) => getColor(status)};
    }
  }
  & span {
    color: ${({ status }) => getColor(status)};
  }
  &:hover {
    background: ${({ status }) => {
      switch (status) {
        case 'selected':
          return Colors.grayOpacity12;
        case 'disabled':
          return '';
        default:
          return Colors.grayOpacity04;
      }
    }};
  }
`;

const NavigationMenu: React.FC<Props> = ({
  icon,
  title,
  onClick,
  link: originalLink,
  activeKey,
  style,
  className,
  node,
  dataType,
  customAttributes,
  disabled,
  tooltipMsg,
  rightSlot,
}) => {
  const { setActiveKey: setKey } = useNavigationStore();
  const router = useRouter();

  const isSelected =
    originalLink &&
    (router.asPath === originalLink ||
      router.asPath.startsWith(`${originalLink}?`) ||
      router.asPath.startsWith(`${originalLink}/`));
  const { isExternalLink, link } = useMemo(() => {
    const isExternalLink =
      originalLink?.startsWith('http://') || originalLink?.startsWith('https://');
    const link = originalLink && !isExternalLink ? appendUtmToUrlString(originalLink) : undefined;
    return { isExternalLink, link };
  }, [originalLink]);

  return (
    <Tooltip title={tooltipMsg}>
      <div>
        <a
          href={link}
          onClick={(e) => {
            if (disabled) {
              e.preventDefault();
              return;
            }

            // 외부 링크인 경우 기본 동작 사용 (target="_blank"로 새 탭에서 열림)
            if (isExternalLink) {
              setKey(activeKey);
              mixpanel.track(MIXPANEL_EVENT.NAV.ITEM_CLICK, {
                title,
              });
              onClick?.();
              return;
            }

            if (e.ctrlKey || e.metaKey) {
              return; // ctrl+click 시 기본 동작만 수행
            }

            e.preventDefault();
            setKey(activeKey);

            mixpanel.track(MIXPANEL_EVENT.NAV.ITEM_CLICK, {
              title,
            });

            if (dataType === 'analysis') {
              mixpanel.track(MIXPANEL_EVENT.ANALYSIS.PRESET_CLICK, {
                title,
              });
            }

            if (link) {
              router.push(link);
            }

            onClick?.();
          }}
          target={isExternalLink ? '_blank' : undefined}
          rel={isExternalLink ? 'noopener noreferrer' : undefined}
          style={{
            display: 'block',
            width: '100%',
            pointerEvents: disabled ? 'none' : 'auto',
            textDecoration: 'none',
          }}
          {...customAttributes}
        >
          <Wrapper
            status={isSelected ? 'selected' : 'default'}
            align="center"
            gap={4}
            style={style}
            className={cx(
              className,
              disabled &&
                css`
                  & span {
                    color: ${Colors.grayOpacity36};
                  }
                  & svg {
                    path {
                      fill: ${Colors.grayOpacity36};
                    }
                  }
                `,
            )}
          >
            {node || (
              <>
                {icon}
                <Text textStyle={`14_${isSelected ? '500' : '400'}`}>{title}</Text>
              </>
            )}
            {rightSlot}
          </Wrapper>
        </a>
      </div>
    </Tooltip>
  );
};

export default NavigationMenu;
